import { Badge, Box, Button, Card, Flex, Text } from '@nrx/ui';
import type { JSX } from 'react';
import { useState } from 'react';

import type { GroupConsultEvent } from './group-consult-calendar.js';
import {
  eventGroupKey,
  formatEventTimeRangeCompact,
  groupEventsByTitleAndDescription,
} from './group-consult-calendar.js';

type GroupConsultListViewProps = {
  events: GroupConsultEvent[];
  timeZone: string;
  showRegisterButton: boolean;
  registerLoading: boolean;
  onRegisterClick: (event: GroupConsultEvent) => void;
};

const DESCRIPTION_PREVIEW_LENGTH = 175;

/**
 * Render the existing grouped session cards for users who prefer the original list view.
 *
 * @remarks The session-group title comes from `Card title` + `headingLevel={4}`, so the heading is
 * the primitive's rather than a hand-rolled `<h4>` (W1/W5). The specs find it with
 * `getByText('Nutrition Basics')`, which matches an element's DIRECT text nodes — `card__title`
 * holds the title and nothing else, so that still resolves.
 *
 * "Limited availability" is a `Badge variant="warning"`, and registration is `Button busy`
 * (`aria-busy` + forced `disabled`) rather than a hand-rolled `disabled` + label swap (W4).
 */
export function GroupConsultListView({
  events,
  timeZone,
  showRegisterButton,
  registerLoading,
  onRegisterClick,
}: GroupConsultListViewProps): JSX.Element {
  return (
    <Flex direction="column" gap="4">
      {groupEventsByTitleAndDescription(events).map((group) => {
        // `groupEventsByTitleAndDescription` never yields an empty group; the guard is only here
        // because `noUncheckedIndexedAccess` types `group[0]` as possibly undefined.
        const first = group[0];
        if (!first) return null;

        return (
          <Card
            key={eventGroupKey(first)}
            title={first.title}
            headingLevel={4}
            elevation="flat"
            className="consult-session-group"
          >
            {first.description ? <ExpandableDescription description={first.description} /> : null}
            <Text as="p" size="2" weight="medium" className="consult-session-group__label">
              Available sessions
            </Text>
            <ul className="consult-session-list">
              {group.map((event) => (
                <li key={event.id}>
                  <Flex align="start" justify="between" gap="4">
                    <Box className="consult-session-list__when">
                      <Text as="div" size="2" color="muted">
                        {formatEventTimeRangeCompact(event.start, event.end, timeZone)}
                      </Text>
                      {event.limitedAvailability ? (
                        <Badge variant="warning" className="consult-session-list__badge">
                          Limited availability
                        </Badge>
                      ) : null}
                    </Box>
                    {showRegisterButton ? (
                      <Button
                        busy={registerLoading}
                        onClick={() => onRegisterClick(event)}
                        className="consult-register-button"
                      >
                        {registerLoading ? 'Registering...' : 'Register'}
                      </Button>
                    ) : null}
                  </Flex>
                </li>
              ))}
            </ul>
          </Card>
        );
      })}
    </Flex>
  );
}

function ExpandableDescription({ description }: { description: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = description.length > DESCRIPTION_PREVIEW_LENGTH;
  const preview = isExpandable
    ? `${description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}...`
    : description;

  return (
    <Box className="consult-session-group__description">
      <Text as="p" size="2" color="muted" className="consult-preserve-lines">
        {expanded || !isExpandable ? description : preview}
      </Text>
      {isExpandable ? (
        <Button
          tone="link"
          onClick={() => setExpanded((currentExpanded) => !currentExpanded)}
          aria-expanded={expanded}
          className="consult-inline-action"
        >
          {expanded ? 'Read less' : 'Read more'}
        </Button>
      ) : null}
    </Box>
  );
}
